import './document-controller.scss';

import { useEffect } from 'react';
import {
  type Control,
  Controller,
  type FieldPath,
  type FieldValues
} from 'react-hook-form';

import { FileDownload, FileUpload } from '@/components/Shared/ui';
import useToggle from '@/hooks/useToggle';
import EyeIcon from '@/media/icons/eye.svg?react';
import type { FileType } from '@/types/uploadFiles';

type TProps<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
> = {
  afterFileChange?: (file: Partial<FileType> | null, name: TName) => void;
  control?: Control<TFieldValues>;
  downloadable?: boolean;
  file?: Partial<FileType> | null;
  fileLink?: string;
  fileUploadPlaceholder?: string;
  initialFileName?: string;
  isFrozen?: boolean;
  isRequired?: boolean;
  name?: TName;
  preDownloadEvent?: () => Promise<void>;
  title: string;
  uploadable?: boolean;
  visiable?: boolean;
  wrapperClassName?: string;
  maxFileSizeMb?: number;
  oldDownloadMethod?: boolean;
  resend?: {
    resendFile: () => void;
    isResendLoading: boolean;
  };
};

const DocumentController = <
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>
>({
  afterFileChange,
  control,
  downloadable = false,
  file,
  fileLink = '',
  fileUploadPlaceholder,
  initialFileName,
  isFrozen = false,
  isRequired = false,
  name,
  preDownloadEvent,
  title,
  uploadable = false,
  visiable = true,
  wrapperClassName = 'financing-step__field',
  resend,
  maxFileSizeMb = 1,
  oldDownloadMethod = false
}: TProps<TFieldValues, TName>) => {
  const fileChangedState = useToggle();

  useEffect(() => {
    fileChangedState.set(!!file);
  }, [file]);

  if (!visiable) return null;

  const handleOpenFile = (field: FieldValues) => {
    const { base64, name } = field.value;
    if (!base64) return;
    const ext = name?.split('.').pop()?.toLowerCase();
    let mime = 'application/octet-stream';
    if (ext === 'pdf') mime = 'application/pdf';
    else if (['jpg', 'jpeg', 'png', 'webp', 'gif', 'bmp'].includes(ext || '')) {
      mime = `image/${ext === 'jpg' ? 'jpeg' : ext}`;
    }
    const byteChars = atob(base64);
    const byteArray = new Uint8Array(byteChars.length);
    for (let i = 0; i < byteChars.length; i++) {
      byteArray[i] = byteChars.charCodeAt(i);
    }
    const blob = new Blob([byteArray], { type: mime });
    const url = URL.createObjectURL(blob);
    window.open(url, '_blank');
  };

  if (uploadable && control && name) {
    return (
      <Controller
        name={name}
        control={control}
        rules={{
          required: isRequired ? 'Пожалуйста, выберите файл' : undefined,
          validate: (value) => {
            if (!value?.base64 && isRequired)
              return 'Пожалуйста, выберите файл';
            if ((value?.size || 0) > maxFileSizeMb * 1024 * 1024)
              return `Файл не должен превышать ${maxFileSizeMb} МБ`;
          }
        }}
        render={({ field, fieldState: { error } }) => (
          <div className={wrapperClassName}>
            <FileUpload
              {...field}
              title={title}
              file={field.value}
              initialFile={
                initialFileName
                  ? {
                      base64: '',
                      name: initialFileName,
                      size: 0
                    }
                  : undefined
              }
              placeholder={fileUploadPlaceholder}
              onChange={(file) => {
                field.onChange(file);
                afterFileChange?.(file, name);
                fileChangedState.setOn();
              }}
              onDelete={() => {
                field.onChange(null);
                afterFileChange?.(null, name);
                fileChangedState.setOff();
              }}
              isRequired={isRequired}
              accept=".pdf"
              allowedExtensions={['pdf']}
              error={error?.message}
              isFrozen={isFrozen}
            />
            {downloadable && !fileChangedState.isOn && (
              <FileDownload
                prevStateTitle={title}
                fileLink={fileLink}
                iconOnly
                preDownloadEvent={preDownloadEvent}
                resend={resend}
                oldDownloadMethod={oldDownloadMethod}
              />
            )}
            {!!field.value?.base64 && (
              <button
                type="button"
                className="document-controller__open-btn"
                onClick={() => handleOpenFile(field)}
                title="Открыть документ"
              >
                <EyeIcon className="document-controller__open-btn-icon" />
              </button>
            )}
          </div>
        )}
      />
    );
  }

  if (downloadable)
    return (
      <div className={wrapperClassName}>
        <FileDownload
          prevStateTitle={title}
          fileLink={fileLink}
          preDownloadEvent={preDownloadEvent}
          resend={resend}
          oldDownloadMethod={oldDownloadMethod}
        />
      </div>
    );

  return null;
};

export default DocumentController;
